import { taskIsSleeping } from "../../store/taskAttention"
import type { TaskAttention } from "../../persistence/records"
import type { SidebarPullRequestAutomation, SidebarTaskDisplayEvent } from "./taskDisplayTypes"

const zeroTime = new Date(0).toISOString()

export interface SidebarSortableTask {
    id: string
    closed?: boolean
    closedAt?: string
    createdAt: string
    updatedAt?: string
    lastActivityAt?: string
    lastViewedAt?: string
    lastEvent?: SidebarTaskDisplayEvent
    scheduledCount?: number
    attention?: TaskAttention
    sleeping?: boolean
    pullRequestAutomation?: SidebarPullRequestAutomation
}

/** Quiet PR automation parks an open task below active work without closing it. */
export function sidebarTaskParked(task: Pick<SidebarSortableTask, "closed" | "pullRequestAutomation" | "attention" | "sleeping">): boolean {
    if (task.attention) return task.sleeping ?? taskIsSleeping(task)
    // Older nodes still project PR waiting without shared attention state.
    const phase = task.pullRequestAutomation?.phase
    return !task.closed && (phase === "watching" || phase === "queued")
}

interface SidebarTaskQueue<T extends SidebarSortableTask> {
    workspaceId: string
    entries: T[]
}

const topBlendLimit = 20
const topBlendQuotas = [5, 4, 3] as const

export function sidebarTaskActivityAt(task: SidebarSortableTask): string {
    return [task.lastEvent?.at, task.lastActivityAt, task.createdAt].reduce<string>((latest, at) => (at && at > latest ? at : latest), zeroTime)
}

export function compareSidebarTasksByRecent(a: SidebarSortableTask, b: SidebarSortableTask): number {
    return sidebarTaskActivityAt(b).localeCompare(sidebarTaskActivityAt(a))
}

function withRunningFirst<T extends SidebarSortableTask>(entries: T[], runningTaskIds: Set<string>): T[] {
    const running = entries.filter((task) => runningTaskIds.has(task.id))
    const idle = entries.filter((task) => !runningTaskIds.has(task.id))
    return [...running, ...idle]
}

export function sortSidebarTasks<T extends SidebarSortableTask>(
    entries: T[],
    options: {
        pinnedTaskIds?: Iterable<string>
        runningTaskIds?: Iterable<string>
    } = {}
): T[] {
    const pinnedSet = new Set(options.pinnedTaskIds ?? [])
    const runningSet = new Set(options.runningTaskIds ?? [])

    const openEntries = entries.filter((task) => !task.closed && !sidebarTaskParked(task)).sort((a, b) => compareSidebarTasksByRecent(a, b))
    const pinnedOpen = withRunningFirst(
        openEntries.filter((task) => pinnedSet.has(task.id)),
        runningSet
    )
    const unpinnedOpen = withRunningFirst(
        openEntries.filter((task) => !pinnedSet.has(task.id)),
        runningSet
    )
    const parkedEntries = entries.filter((task) => sidebarTaskParked(task)).sort((a, b) => compareSidebarTasksByRecent(a, b))
    const pinnedParked = parkedEntries.filter((task) => pinnedSet.has(task.id))
    const unpinnedParked = parkedEntries.filter((task) => !pinnedSet.has(task.id))
    const closedEntries = entries.filter((task) => task.closed).sort((a, b) => compareSidebarTasksByRecent(a, b))
    const pinnedClosed = closedEntries.filter((task) => pinnedSet.has(task.id))
    const unpinnedClosed = closedEntries.filter((task) => !pinnedSet.has(task.id))

    return [...pinnedOpen, ...unpinnedOpen, ...pinnedParked, ...unpinnedParked, ...pinnedClosed, ...unpinnedClosed]
}

export function blendSidebarTaskQueues<T extends SidebarSortableTask>(
    queues: SidebarTaskQueue<T>[],
    options: {
        pinnedTaskIds?: Iterable<string>
        runningTaskIds?: Iterable<string>
    } = {}
): T[] {
    const pinnedSet = new Set(options.pinnedTaskIds ?? [])
    const runningSet = new Set(options.runningTaskIds ?? [])
    const pinnedOpenEntries = sortSidebarTasks(
        queues.flatMap((queue) => queue.entries.filter((entry) => !entry.closed && !sidebarTaskParked(entry) && pinnedSet.has(entry.id))),
        options
    )
    const rankedQueues = queues
        .map((queue, sourceIndex) => {
            const projectOpenEntries = sortSidebarTasks(queue.entries, options).filter((entry) => !entry.closed && !sidebarTaskParked(entry))
            const openEntries = projectOpenEntries.filter((entry) => !pinnedSet.has(entry.id))
            const latestActivityAt = projectOpenEntries.reduce((latest, entry) => {
                const activityAt = sidebarTaskActivityAt(entry)
                return activityAt > latest ? activityAt : latest
            }, zeroTime)
            return {
                cursor: 0,
                latestActivityAt,
                openCount: projectOpenEntries.length,
                openEntries,
                runningCount: projectOpenEntries.filter((entry) => runningSet.has(entry.id)).length,
                sourceIndex,
                workspaceId: queue.workspaceId,
            }
        })
        .filter((queue) => queue.openEntries.length > 0)
        .sort((a, b) => {
            if (a.runningCount !== b.runningCount) return b.runningCount - a.runningCount
            const activityOrder = b.latestActivityAt.localeCompare(a.latestActivityAt)
            if (activityOrder !== 0) return activityOrder
            if (a.openCount !== b.openCount) return b.openCount - a.openCount
            return a.sourceIndex - b.sourceIndex
        })

    const guaranteedQueues = rankedQueues.slice(0, topBlendLimit)
    const quotaForRank = (rank: number): number => topBlendQuotas[rank] ?? 1
    const topWindowTarget = Math.min(
        topBlendLimit,
        guaranteedQueues.reduce((total, _queue, rank) => total + quotaForRank(rank), 0),
        rankedQueues.reduce((total, queue) => total + queue.openEntries.length, 0)
    )
    const blendedOpen: T[] = []

    for (let round = 0; round < topBlendQuotas[0] && blendedOpen.length < topWindowTarget; round += 1) {
        for (const [rank, queue] of guaranteedQueues.entries()) {
            if (round >= quotaForRank(rank) || queue.cursor >= queue.openEntries.length) continue
            blendedOpen.push(queue.openEntries[queue.cursor])
            queue.cursor += 1
            if (blendedOpen.length >= topWindowTarget) break
        }
    }

    const nextQueueByRecentHead = () => {
        let selected: (typeof rankedQueues)[number] | undefined
        for (const queue of rankedQueues) {
            if (queue.cursor >= queue.openEntries.length) continue
            if (!selected) {
                selected = queue
                continue
            }
            const activityOrder = compareSidebarTasksByRecent(queue.openEntries[queue.cursor], selected.openEntries[selected.cursor])
            if (activityOrder < 0 || (activityOrder === 0 && queue.sourceIndex < selected.sourceIndex)) selected = queue
        }
        return selected
    }

    while (true) {
        const queue = nextQueueByRecentHead()
        if (!queue) break
        blendedOpen.push(queue.openEntries[queue.cursor])
        queue.cursor += 1
    }

    const parkedEntries = sortSidebarTasks(
        queues.flatMap((queue) => queue.entries.filter((entry) => sidebarTaskParked(entry))),
        options
    )
    const closedEntries = sortSidebarTasks(
        queues.flatMap((queue) => queue.entries.filter((entry) => entry.closed)),
        options
    )
    return [...pinnedOpenEntries, ...blendedOpen, ...parkedEntries, ...closedEntries]
}
